// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useCallback, useState } from 'react';

import { Text } from '@mantine/core';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import type { LOCAL_STORAGE_KEY, LOCAL_STORAGE_TYPE } from 'constants/globalConstants';
import { isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';
import { useLocalStorage } from 'hooks/useLocalStorage';

type EditableProps<T> = {
  selectedItem: T | null;
  selectItem: (item: T | null, force?: boolean) => void;
  editing: boolean;
  setEditing: (editing: boolean) => void;
};

export const EditableWrapper = <K extends Extract<LOCAL_STORAGE_KEY, 'LAST_VIEWED_PROJECT' | 'LAST_VIEWED_FACILITY'>>({
  children,
  localStorageKey,
}: {
  children: (props: EditableProps<LOCAL_STORAGE_TYPE[K]>) => ReactNode;
  localStorageKey: K;
}) => {
  const [editing, setEditing] = useState(false);
  const [showEditWarning, setShowEditWarning] = useState(false);
  useBlockerWhen(`editable-wrapper-${localStorageKey}`, editing);

  const [storedItem, setSelectedItem] = useLocalStorage(localStorageKey, null);
  // TODO(FWP-1728): remove after 2026-12-01 — two months past deploy, by which point every session
  // predating the move has expired (JWTs live for one month). Guards against LAST_VIEWED_* values
  // cached before projects/facilities moved to the shop API (old shape had `assignees`/`address`,
  // no `members`).
  const selectedItem = isNotNil(storedItem) && !('members' in storedItem) ? null : storedItem;

  const selectItem = useCallback(
    (item: typeof selectedItem, force = false) => {
      if (!force && editing) {
        setShowEditWarning(true);
        return;
      }
      setSelectedItem(item);
    },
    [editing, setSelectedItem],
  );

  return (
    <>
      {children({
        selectItem,
        selectedItem,
        editing,
        setEditing,
      })}
      <WrappedModal
        title="Edit in progress"
        opened={showEditWarning}
        onConfirm={() => setShowEditWarning(false)}
        withCancelButton={false}
      >
        <Text>Please save or discard your changes before continuing.</Text>
      </WrappedModal>
    </>
  );
};
